import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Crown, X } from 'lucide-react';
import { useEntitlements, FREE_MATCH_LIMIT } from '@/hooks/useEntitlements';
import UpgradeDialog from './UpgradeDialog';

const STORAGE_KEY = 'luvall-match-limit-nudge';

/**
 * Soft upgrade nudge shown once when free users approach the 50-match cap.
 * Dismissed permanently via localStorage.
 */
export const MatchLimitBanner: React.FC = () => {
  const ent = useEntitlements();
  const [dismissed, setDismissed] = useState(true);
  const [showUpgrade, setShowUpgrade] = useState(false);

  useEffect(() => {
    setDismissed(localStorage.getItem(STORAGE_KEY) === '1');
  }, []);

  if (!ent.shouldNudge || dismissed) return null;

  const dismiss = () => {
    localStorage.setItem(STORAGE_KEY, '1');
    setDismissed(true);
  };

  return (
    <>
      <Card className="mb-4 border-primary/40 bg-primary/5">
        <CardContent className="p-3 flex items-start gap-3">
          <div className="p-2 rounded-full bg-primary/10 text-primary shrink-0">
            <Crown className="h-4 w-4" />
          </div>
          <div className="flex-1 text-sm">
            <p className="font-medium">
              {ent.matchesRemaining} free {ent.matchesRemaining === 1 ? 'match' : 'matches'} left
            </p>
            <p className="text-muted-foreground text-xs">
              You've played {ent.matchesPlayed} of {FREE_MATCH_LIMIT}. Upgrade from €3/month to keep playing.
            </p>
            <Button size="sm" className="mt-2 min-h-[36px]" onClick={() => setShowUpgrade(true)}>
              Upgrade to Premium
            </Button>
          </div>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={dismiss}
            className="p-1 text-muted-foreground hover:text-foreground min-h-[32px] min-w-[32px]"
          >
            <X className="h-4 w-4" />
          </button>
        </CardContent>
      </Card>
      <UpgradeDialog open={showUpgrade} onOpenChange={setShowUpgrade} />
    </>
  );
};

export default MatchLimitBanner;
